import React, { useState, useRef, useEffect } from "react";
import {
  HomeIcon,
  Squares2X2Icon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { User } from "@supabase/supabase-js";
import styles from "./AccountMenu.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import SubscriptionStatusChip from "./SubscriptionStatusChip";
import ThemeToggle from "./ThemeToggle";

/**
 * Represents a single menu item in the AccountMenu.
 */
export interface MenuItem {
  /** Display label */
  label: string;
  /** Icon component shown before the label */
  icon: React.ReactNode;
  /** Optional path to navigate to on click */
  to?: string;
  /** Optional custom click handler (overrides `to`) */
  onClick?: () => void;
}

interface AccountMenuProps {
  /**
   * Menu items to render. If omitted, sensible defaults will be used.
   * Provide different sets on each page so the menu is context-aware.
   */
  menuItems?: MenuItem[];
  language?: "en" | "ar";
}

/**
 * Dropdown menu anchored to the "My Account" trigger in the header.
 * Shows the user e-mail, remaining credits, a configurable list of links,
 * and a styled logout button.
 */
const AccountMenu: React.FC<AccountMenuProps> = ({ menuItems, language }) => {
  const isArabic = language === "ar";
  const labels = isArabic
    ? {
        openMenu: "فتح قائمة الحساب",
        greeting: "مرحباً بعودتك!",
        noEmail: "(لا يوجد بريد إلكتروني)",
        credits: "الرصيد",
        theme: "المظهر",
        logout: "تسجيل الخروج",
        logoutSuccess: "تم تسجيل خروجك بنجاح. نراك قريباً!",
        logoutError: "تعذر تسجيل الخروج. حاول مرة أخرى.",
        home: "الرئيسية",
        account: "حسابي",
      }
    : {
        openMenu: "Open account menu",
        greeting: "Welcome back!",
        noEmail: "(no-email)",
        credits: "Credits",
        theme: "Theme",
        logout: "Log out",
        logoutSuccess: "👋 You've successfully signed out. See you next time!",
        logoutError: "Failed to sign out. Please try again.",
        home: "Home",
        account: "My Account",
      };
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [menuPosition, setMenuPosition] = useState<"bottom" | "top">("bottom");

  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  // ---------------------------------------------------------------------------
  // Credits handling
  // ---------------------------------------------------------------------------
  const [credits, setCredits] = useState<number | null>(null);
  // ---------------------------------------------------------------------------
  // Subscription handling
  // ---------------------------------------------------------------------------
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);

  // Cache refs to prevent refetching on each render/interaction
  const lastFetchTimeRef = useRef<number>(0);
  const isFetchingRef = useRef<boolean>(false);
  const CACHE_DURATION_MS = 30000; // 30 seconds cache

  useEffect(() => {
    const loadData = async () => {
      if (!user?.id) return;

      // Skip if already fetching or if cache is still fresh
      const now = Date.now();
      if (isFetchingRef.current) return;
      if (now - lastFetchTimeRef.current < CACHE_DURATION_MS) return;

      isFetchingRef.current = true;

      try {
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

        // Fetch both credits and subscription in parallel
        const [creditsRes, subRes] = await Promise.all([
          authenticatedFetch(`${apiUrl}/api/credits/${user.id}`),
          authenticatedFetch(`${apiUrl}/api/subscriptions/${user.id}`)
        ]);

        if (creditsRes.ok) {
          const data = await creditsRes.json();
          setCredits(data.credits ?? 0);
        }

        if (subRes.ok) {
          const data = await subRes.json();
          if (data && data.status === "active") {
            setSubscriptionPlan(data.plan);
          } else {
            setSubscriptionPlan(null);
          }
        }

        lastFetchTimeRef.current = Date.now();
      } catch (err) {
        console.error("Failed to fetch account data", err);
      } finally {
        isFetchingRef.current = false;
      }
    };

    loadData();
  }, [user]);

  // ---------------------------------------------------------------------------
  // Menu position (open above if not enough space below)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen || !menuRef.current || !triggerRef.current) return;

    const dropdownRect = triggerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - dropdownRect.bottom;

    if (spaceBelow < menuHeight && dropdownRect.top > menuHeight) {
      setMenuPosition("top");
    } else {
      setMenuPosition("bottom");
    }
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // Click-outside handler – close menu when clicking elsewhere
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        startCloseAnimation();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  const startCloseAnimation = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsAnimatingOut(false);
    }, 200); // Keep in sync with fadeOut animation duration
  };

  const handleLogout = async () => {
    try {
      await signOut();
      (window as any).showNotification?.(
        labels.logoutSuccess,
        "success"
      );
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
      (window as any).showNotification?.(
        labels.logoutError,
        "error"
      );
    }
  };

  // ---------------------------------------------------------------------------
  // Default menu items (will be filtered/overridden by props if provided)
  // ---------------------------------------------------------------------------
  const defaultItems: MenuItem[] = [
    {
      label: labels.home,
      icon: <HomeIcon width={20} height={20} />,
      to: "/",
    },
    {
      label: labels.account,
      icon: <Squares2X2Icon width={20} height={20} />,
      to: "/dashboard",
    },
  ];

  const itemsToRender = menuItems ?? defaultItems;

  // ---------------------------------------------------------------------------
  // Helpers for avatar
  // ---------------------------------------------------------------------------
  const getInitials = (user: User | null): string => {
    const name = user?.user_metadata?.full_name as string | undefined;
    if (name) {
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name[0].toUpperCase();
    }
    if (!user?.email) return "?";
    return user.email.charAt(0).toUpperCase();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div style={{ position: "relative" }}>
      {/* Trigger */}
      <button
        type="button"
        ref={triggerRef}
        className={styles.accountTrigger}
        onClick={() => (isOpen ? startCloseAnimation() : setIsOpen(true))}
        aria-label={labels.openMenu}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <UserCircleIcon className={styles.accountIcon} aria-hidden="true" />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={menuRef}
          className={`${styles.menuContainer} ${isAnimatingOut ? styles.fadeOut : ""}`}
          style={{
            position: "absolute",
            ...(isArabic ? { left: 0 } : { right: 0 }),
            ...(menuPosition === "bottom" ? { top: "100%", marginTop: "8px" } : { bottom: "100%", marginBottom: "8px" }),
          }}
        >
          {/* Profile header */}
          <div className={styles.profileHeader}>
            <div className={styles.userInfo}>
              <div className={styles.avatar}>
                {getInitials(user)}
              </div>
              <div className={styles.userDetails}>
                <div className={styles.email}>
                  {user?.user_metadata?.full_name || user?.email || labels.noEmail}
                </div>
                <div className={styles.greeting}>{labels.greeting}</div>
              </div>
            </div>

            {/* Credits or Subscription Badge */}
            {subscriptionPlan ? (
              <SubscriptionStatusChip plan={subscriptionPlan} className={styles.subscriptionBadge} />
            ) : (
              <div className={styles.statusBadge}>
                <svg className={styles.statusIcon} viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 1a6 6 0 00-3.815 10.631C7.237 12.5 8 13.443 8 14.5v.5a2 2 0 002 2 2 2 0 002-2v-.5c0-1.057.763-2 1.815-2.869A6 6 0 0010 1zM8 17.5a1 1 0 102 0h-2z" />
                </svg>
                <span className={styles.statusText}>
                  {labels.credits}: <span className={styles.statusValue}>{credits ?? "..."}</span>
                </span>
              </div>
            )}
          </div>

          {/* Menu Content */}
          <div className={styles.menuContent}>
            {/* Menu items list */}
            <div className={styles.menuItems}>
              {itemsToRender.map((item) => (
                <div
                  key={item.label}
                  className={styles.menuItem}
                  onClick={() => {
                    startCloseAnimation();
                    if (item.onClick) {
                      item.onClick();
                    } else if (item.to) {
                      navigate(item.to);
                    }
                  }}
                >
                  <span className={styles.menuItemIcon}>{item.icon}</span>
                  <span className={styles.menuItemText}>{item.label}</span>
                </div>
              ))}
            </div>

            <div className={styles.themePickerRow}>
              <span className={styles.themePickerLabel}>{labels.theme}</span>
              <ThemeToggle className={styles.themePickerControl} language={language} />
            </div>

            {/* Divider */}
            <div className={styles.divider} />

            {/* Logout button */}
            <button className={styles.logoutButton} onClick={handleLogout}>
              <svg className={styles.logoutIcon} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 4.25A2.25 2.25 0 015.25 2h5.5A2.25 2.25 0 0113 4.25v2a.75.75 0 01-1.5 0v-2a.75.75 0 00-.75-.75h-5.5a.75.75 0 00-.75.75v11.5c0 .414.336.75.75.75h5.5a.75.75 0 00.75-.75v-2a.75.75 0 011.5 0v2A2.25 2.25 0 0110.75 18h-5.5A2.25 2.25 0 013 15.75V4.25z" clipRule="evenodd" />
                <path fillRule="evenodd" d="M6 10a.75.75 0 01.75-.75h9.546l-1.048-.943a.75.75 0 111.004-1.114l2.5 2.25a.75.75 0 010 1.114l-2.5 2.25a.75.75 0 11-1.004-1.114l1.048-.943H6.75A.75.75 0 016 10z" clipRule="evenodd" />
              </svg>
              {labels.logout}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default AccountMenu;
